import { MdOutlineFileUpload } from "react-icons/md";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { extractPictureFilename, resolveConnectorAssetPublicUrl, resolveFieldImageUrls } from "./utils/pictureUtils";
import ImagePickerDialog from "./component/ImagePickerDialog";
import { getApiBaseUrl, removeFileLink, uploadConnectorImage } from "./api/file";
import { useAuth } from "./auth/AuthContext";

type ImageProps = {
    filename?: unknown;
    alt: string;
    parent?: any;
    onChange?: (data: { filename: string }) => void;
    connectorAssetPublicUrl?: string | null;
    readOnly?: boolean;
    /** Allow uploading a new image to the remote connector server (edit forms only). */
    allowConnectorUpload?: boolean;
    /** Collection slug for the upload API (defaults to Redux current collection/slug). */
    collectionSlug?: string;
    /** Field name used for relation tracking (e.g. picture/image). */
    fieldName?: string;
    /** Classes for the thumbnail. Defaults to a small square. */
    thumbClassName?: string;
};

const Image = ({
    filename,
    alt,
    parent,
    onChange,
    connectorAssetPublicUrl,
    readOnly = false,
    allowConnectorUpload = false,
    collectionSlug,
    fieldName,
    thumbClassName = "h-10 w-10 rounded-xl object-cover",
}: ImageProps) => {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [thumbFailed, setThumbFailed] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [pickedFilename, setPickedFilename] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const _currentItem = useSelector((state: any) => state.item.current);
    const slug = useSelector((state: any) => state.item.currentSlug);
    const collection = useSelector((state: any) => state.item.currentCollection);
    const tableSlug = useSelector((state: any) => state.table.currentSlug);
    const reduxUser = useSelector((state: any) => state.user);
    const { user: authUser } = useAuth();
    const currentItem = _currentItem;
    const effectiveUserId = authUser?.uid || reduxUser?.uid || reduxUser?.info?.uid;

    const resolvedFilename =
        extractPictureFilename(pickedFilename ?? filename) || pickedFilename || filename;

    useEffect(() => {
        setPickedFilename(null);
    }, [filename]);
    const assetBaseUrl =
        connectorAssetPublicUrl ||
        resolveConnectorAssetPublicUrl({
            slug: slug || tableSlug,
            source: parent?.source,
        });
    const { src, thumb } = resolveFieldImageUrls(resolvedFilename, {
        connectorAssetPublicUrl: assetBaseUrl,
    });
    const hasFilename = Boolean(extractPictureFilename(resolvedFilename));
    const hasImage = hasFilename && Boolean(src || thumb);
    const [previewIndex, setPreviewIndex] = useState(0);

    const uploadApiBase = getApiBaseUrl();
    const encodedPath = typeof resolvedFilename === "string"
        ? resolvedFilename
            .replace(/^\/+/, "")
            .split("/")
            .map((segment) => encodeURIComponent(segment))
            .join("/")
        : "";
    const fallbackCandidates = [
        thumb,
        src,
        encodedPath ? `/uploads/${encodedPath}` : "",
        encodedPath ? `/api/uploads/${encodedPath}` : "",
        encodedPath && uploadApiBase ? `${uploadApiBase}/uploads/${encodedPath}` : "",
        encodedPath && uploadApiBase ? `${uploadApiBase.replace(/\/api$/, "")}/api/uploads/${encodedPath}` : "",
    ].filter(Boolean);
    const previewSrc = fallbackCandidates[previewIndex] || "";

    useEffect(() => {
        setThumbFailed(false);
    }, [thumb]);
    useEffect(() => {
        setPreviewIndex(0);
    }, [thumb, src, resolvedFilename]);

    const collectionForUpload = collectionSlug || collection || slug || tableSlug;
    const canConnectorUpload = Boolean(
        assetBaseUrl && allowConnectorUpload && !readOnly && collectionForUpload
    );

    const connectorKey: string | undefined =
        parent?.source?.connectorKey || undefined;

    const canOpenPicker = !readOnly && Boolean(onChange) && !(assetBaseUrl && !connectorKey);

    const handleClickChange = () => {
        if (!canOpenPicker) return;
        setPreviewOpen(false);
        setPickerOpen(true);
    };

    const handleSelect = async (data: { filename: string }) => {
        const previousName = extractPictureFilename(filename);
        const nextName = extractPictureFilename(data.filename);
        const relationCollection = collectionForUpload;
        const relationUid = parent?.uid ?? currentItem?.uid ?? parent?._id ?? currentItem?._id;
        if (
            previousName &&
            nextName &&
            previousName !== nextName &&
            relationCollection &&
            relationUid &&
            fieldName
        ) {
            await removeFileLink({
                collection: relationCollection,
                itemUid: String(relationUid),
                field: fieldName,
                filename: previousName,
            }).catch(() => null);
        }
        setPickedFilename(nextName || data.filename);
        onChange?.(data);
    };

    const handleConnectorFilePicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        setUploading(true);
        setUploadError(null);
        try {
            const result = await uploadConnectorImage(collectionForUpload, file);
            if (result?.success && result.filename) {
                handleSelect({ filename: result.filename });
            } else {
                setUploadError(result?.message || 'Upload failed');
            }
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = async (event?: React.MouseEvent) => {
        event?.preventDefault();
        event?.stopPropagation();
        if (!onChange) return;
        const previousName = extractPictureFilename(resolvedFilename);
        const relationCollection = collectionForUpload;
        const relationUid = parent?.uid ?? currentItem?.uid ?? parent?._id ?? currentItem?._id;
        if (previousName && relationCollection && relationUid && fieldName) {
            await removeFileLink({
                collection: relationCollection,
                itemUid: String(relationUid),
                field: fieldName,
                filename: previousName,
            }).catch(() => null);
        }
        setPickedFilename("");
        setPreviewOpen(false);
        onChange({ filename: "" });
    };

    const canRemoveImage = Boolean(hasImage && onChange && !readOnly);
    const canChangeImage = canOpenPicker || canConnectorUpload;

    const openPreview = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setPreviewOpen(true);
    };

    const handleChangeFromPreview = () => {
        if (canOpenPicker) {
            handleClickChange();
            return;
        }
        setPreviewOpen(false);
        fileInputRef.current?.click();
    };

    const picker = (
        <ImagePickerDialog
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            onSelect={handleSelect}
            uploadOptions={
                collectionForUpload
                    ? {
                        collection: collectionForUpload,
                        uid: parent?.uid ?? currentItem?.uid ?? parent?._id ?? currentItem?._id,
                        field: fieldName,
                        userId: effectiveUserId
                    }
                    : undefined
            }
            connectorKey={connectorKey}
            connectorAssetPublicUrl={connectorKey ? assetBaseUrl : undefined}
        />
    );

    const connectorUploadControls = canConnectorUpload ? (
        <>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleConnectorFilePicked}
            />
            <button
                type="button"
                title="Upload a new image to the remote server"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center text-gray-500 hover:text-gray-800 disabled:opacity-50"
            >
                {uploading
                    ? <Loader2 className="w-5 h-5 animate-spin" />
                    : <MdOutlineFileUpload className="text-2xl" />}
            </button>
            {uploadError && <span className="text-xs text-red-500">{uploadError}</span>}
        </>
    ) : null;

    if (!hasImage) {
        if (canConnectorUpload) {
            return <span className="inline-flex items-center gap-2">{connectorUploadControls}</span>;
        }
        if (readOnly || assetBaseUrl) {
            return <span className="text-muted-foreground text-sm">—</span>;
        }
        return (
            <>
                <MdOutlineFileUpload onClick={handleClickChange} className="cursor-pointer text-2xl" />
                {picker}
            </>
        );
    }

    const fullSrc = previewSrc || src;

    return (
        <span className="inline-flex items-center gap-2">
            <button
                type="button"
                onClick={openPreview}
                onMouseDown={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
                className="inline-flex shrink-0 cursor-zoom-in rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title="View image"
                aria-label={alt ? `View ${alt}` : "View image"}
            >
                <img
                    src={thumbFailed ? fullSrc : previewSrc || thumb}
                    alt={alt}
                    onError={() => {
                        setThumbFailed(true);
                        setPreviewIndex((i) => (i + 1 < fallbackCandidates.length ? i + 1 : i));
                    }}
                    className={thumbClassName}
                />
            </button>
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>{alt || "Picture"}</DialogTitle>
                    </DialogHeader>
                    <img
                        src={fullSrc}
                        alt={alt}
                        className="max-h-[70vh] w-full rounded-md object-contain bg-muted"
                    />
                    {canChangeImage || canRemoveImage ? (
                        <DialogFooter>
                            {canChangeImage ? (
                                <Button type="button" variant="outline" onClick={handleChangeFromPreview}>
                                    Change
                                </Button>
                            ) : null}
                            {canRemoveImage ? (
                                <Button type="button" variant="destructive" onClick={() => { void handleRemove(); }}>
                                    Remove
                                </Button>
                            ) : null}
                        </DialogFooter>
                    ) : null}
                </DialogContent>
            </Dialog>
            {connectorUploadControls}
            {picker}
        </span>
    );
};

export default Image;
